import type { AxiosResponse } from 'axios'
import { AxiosError } from 'axios'
import * as PDFJS from 'pdfjs-dist/legacy/build/pdf'
import { defineStore } from 'pinia'
import type { ComputedRef } from 'vue'
import { computed, h, ref, render, shallowRef } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import ConfirmModal from '@/components/pdf/confirmModal.vue'

import { annotationMapper, getEntries, waitForElement } from '@/lib/utils'
import { useSignatures } from './signatureStore'
import { CMAP_URL, CMAP_PACKED } from '@/utils/pdfjs'

import type {
  AnnotationObject,
  AnnotationStorage,
  AnnotationUpdate,
  ClientActionDocument,
  FieldObject,
  FieldObjects,
  RequestFormPayload,
  SubmitDocumentPayload
} from '@/types'
import clientactionService from '@/services/clientaction.service'
import ClientActionService from '@/services/clientaction.service'
import { useClientActionStore } from '@/store/clientActionStore'
import { type FormsPdfFormSubmitCreateData, FormsService } from '@/types/generated'
import { ROUTES } from '@/router/routes.ts'

type PrefillDataMap = {
  client_first_name?: string
  client_last_name?: string
  client_email?: string
  client_phone?: string
  probably_client_city?: string
  probably_client_zip?: string
  probably_client_street?: string
  probably_client_street_number?: string
  probably_client_date_of_birth?: string
  [key: string]: string | undefined
}

export const usePDF = defineStore('pdf', () => {
  const route = useRoute()
  const router = useRouter()
  const clientActionStore = useClientActionStore()
  // const documentStore = useDocuments()
  const signatureStore = useSignatures()

  // Current PDF info (VuePDF)
  // @ts-ignore
  const loadingTask = shallowRef<PDFJS.PDFDocumentLoadingTask | undefined>()
  const fingerprints = shallowRef<string[]>([])
  const pages = shallowRef<number>(1)

  // Current PDF info
  const page = ref<number>(1)
  const requiredFields = ref<(string | null)[]>([])

  // Current PDF state
  const ready = ref<boolean>(false)
  const hasChanges = ref<boolean>(false)
  const hasSubmitted = ref<boolean>(false)
  const isLoadingPDF = ref<boolean>(false)
  const showConfirmModal = ref<boolean>(false)
  const waitingForServerResponse = ref<boolean>(false)

  const selectedForm = ref<ClientActionDocument>()
  const annotationStorage = ref<AnnotationStorage | undefined>()
  const annotationStorages = ref<Map<string, AnnotationStorage>>(new Map())
  const loadingProgress = ref(0)
  const initDone = ref(false)
  const access_token = ref<string | null>(null)
  const session_token = ref<string | null>(null)
  const prefilledData = ref<PrefillDataMap | null>(null)

  /*
   * Lookup table for fieldObjects by id or name.
   */
  const lt = {
    byId: new Map<string, string>(),
    byName: new Map<string, string>()
  }

  /*
   *  Wait for all annotationLayer form elements to be loaded into the DOM.
   */
  async function waitForAnnotations(fieldObjects: FieldObjects): Promise<Element[]> {
    if (!fieldObjects) {
      console.warn('No fieldObjects (waitForAnnotations)')
      return []
    }

    const cleanupFunctions: (() => void)[] = []

    try {
      const elements = await Promise.all(
        Object.values(fieldObjects)
          .flat()
          .filter((fieldObject: FieldObject) => fieldObject.page + 1 === page.value)
          .map((fieldObject: FieldObject) => {
            const { promise, cancel } = waitForElement(
              `section[data-annotation-id="${fieldObject.id}"]`
            )
            cleanupFunctions.push(cancel)
            return promise
          })
      )
      return elements
    } finally {
      cleanupFunctions.forEach((cleanup) => cleanup())
    }
  }

  async function selectForm(
    form: ClientActionDocument,
    prefillData?: PrefillDataMap
  ): Promise<ClientActionDocument | Error> {
    // if (!isRequestableDocumentForm(form)) {
    //     console.warn("Can't select form", form)
    //     return Promise.reject(new Error('Form not found.'))
    // }
    try {
      isLoadingPDF.value = true
      selectedForm.value = form
      if (prefillData) {
        prefilledData.value = prefillData
      }
      await loadPDF(form.documentProperties.download.url)
      return form
    } finally {
      isLoadingPDF.value = false
    }
  }

  function resetForm(): void {
    confirmDialog('Möchten Sie das Dokument zurücksetzen?')
      .then(() => {
        initClientResponse()
      })
      .catch(() => {
        console.info('The user cancelled the reset form action.')
      })
  }

  ///////////////////////////////////////////////////////////////////////////////////
  /////  CURRENT PDF STATE  /////////////////////////////////////////////////////////
  ///////////////////////////////////////////////////////////////////////////////////

  const hasAllRequired: ComputedRef<boolean> = computed(() => {
    if (!annotationStorage.value) {
      return true
    }
    return requiredFields.value.every(
      (x) => x && annotationStorage.value && annotationStorage.value[x]?.value
    )
  })

  const editable: ComputedRef<boolean> = computed(() => {
    if (!selectedForm.value) return false
    return selectedForm.value.documentProperties.tag !== 'SIGNED'
  })

  ///////////////////////////////////////////////////////////////////////////////////
  /////  CURRENT PDF  ///////////////////////////////////////////////////////////////
  ///////////////////////////////////////////////////////////////////////////////////

  function prevPage(): void {
    page.value = page.value > 1 ? page.value - 1 : page.value
  }

  function nextPage(): void {
    if (!loadingTask.value) {
      return
    }
    page.value = page.value < pages.value ? page.value + 1 : page.value
  }

  function gotoPage(index: number): void {
    page.value = index
  }

  function confirmDialog(message: string): Promise<boolean> {
    return new Promise((resolve, reject) => {
      const confirmModal = h(ConfirmModal, {
        message,
        onConfirm: () => {
          showConfirmModal.value = false
          resolve(true)
        },
        onCancel: () => {
          showConfirmModal.value = false
          reject(false)
        }
      })
      render(confirmModal, document.getElementById('confirm-modal') as HTMLElement)
      showConfirmModal.value = true
    })
  }

  ///////////////////////////////////////////////////////////////////////////////////
  /////  SEND FORM  /////////////////////////////////////////////////////////////////
  ///////////////////////////////////////////////////////////////////////////////////

  /*
   * Submit the form to the backend AS A LAWFIRM-CLIENT.
   */
  async function submitForm(): Promise<void> {
    const text =
      'Möchten Sie das Dokument abschicken? Dieser Vorgang kann nicht rückgängig gemacht werden.'
    confirmDialog(text)
      .then(async () => {
        waitingForServerResponse.value = true

        const fieldObjects = (await getFieldObjects()) ?? {}
        annotationMapper(fieldObjects, annotationStorage.value)
          .then((annotationData) => {
            // Handle signatures
            // TODO Move to signatureStore
            console.warn('signatureStore.signatures', signatureStore.signatures)
            let signatures = {}
            getEntries(signatureStore.signatures).forEach(([key, value]) => {
              if (!selectedForm.value) {
                console.warn('No selectedForm')
              }
              const selFormId = selectedForm.value?.documentProperties.id ?? ''
              signatures = {
                ...signatures,
                [key.replace(selFormId, '')]: value
              }
            })
            const data: SubmitDocumentPayload = {
              access_token: route.query.access_token?.toString() || '',
              token: route.params.id.toString(),
              signatures,
              annotationData
            }
            clientactionService
              .signPdfDocument(data)
              .then((response: AxiosResponse) => {
                selectedForm.value = response.data.payload.document
                loadPDF(response.data.payload.document.documentProperties.download.url)
                waitingForServerResponse.value = false
                hasSubmitted.value = true
                // For the user to have reached this stage and not have an access token it means they are an external PDF signing.
                // In that case we can route them to the congratulations page once they are done signing
                if (session_token.value || data.access_token !== '') {
                  // Build query params - prefer session_token if available, fallback to access_token
                  const queryParams: Record<string, string | null> = {}
                  if (session_token.value) {
                    queryParams.session_token = session_token.value
                  } else if (access_token.value) {
                    queryParams.access_token = access_token.value
                  }
                  router.push({
                    path: `/client/portal`,
                    query: queryParams
                  })
                } else {
                  router.push({ path: `/client/portal/external/questionnaire/completed` })
                }
              })
              .catch((err: AxiosError) => {
                console.warn(err)
                waitingForServerResponse.value = false
              })
          })
          .catch((err) => {
            console.warn(err)
            waitingForServerResponse.value = false
          })
      })
      .catch(() => {
        console.info('The user cancelled the submit form action.')
        waitingForServerResponse.value = false
      })
  }

  /*
   * Submit the form to the backend AS A LAWFIRM-CLIENT, using the new API.
   */
  async function submitFormV3(): Promise<void> {
    // The confirmation dialog will be handled by the component using this store
    // This function should be called after user confirms
    waitingForServerResponse.value = true

    const fieldObjects = (await getFieldObjects()) ?? {}
    annotationMapper(fieldObjects, annotationStorage.value)
      .then((annotationData) => {
        // Handle signatures
        // TODO Move to signatureStore
        console.warn('signatureStore.signatures', signatureStore.signatures)
        let signatures = {}
        getEntries(signatureStore.signatures).forEach(([key, value]) => {
          if (!selectedForm.value) {
            console.warn('No selectedForm')
          }
          const selFormId = selectedForm.value?.documentProperties.id ?? ''
          signatures = {
            ...signatures,
            [key.replace(selFormId, '')]: value
          }
        })
        const data: FormsPdfFormSubmitCreateData = {
          requestBody: {
            access_token: access_token.value || '',
            annotation_data: annotationData,
            signatures
          },
          token:
            route.params.form_token?.toString() ||
            route.params.uuid?.toString() ||
            route.params.id?.toString() ||
            ''
        }
        FormsService.formsPdfFormSubmitCreate(data)
          .then((response) => {
            selectedForm.value = response
            loadPDF(response.documentProperties.download.url)
            waitingForServerResponse.value = false
            hasSubmitted.value = true
            // For the user to have reached this stage and not have an access token it means they are an external PDF signing.
            // In that case we can route them to the congratulations page once they are done signing
            if (session_token.value || access_token.value) {
              // Build query params - prefer session_token if available, fallback to access_token
              const queryParams: Record<string, string | null> = {}
              if (session_token.value) {
                queryParams.session_token = session_token.value
              } else if (access_token.value) {
                queryParams.access_token = access_token.value
              }
              router.push({
                path: `${ROUTES.CLIENT_PORTAL.HOME}`,
                query: queryParams
              })
            } else {
              router.push({
                path: `${ROUTES.CLIENT_PORTAL.EXTERNAL_PDF_COMPLETED}`,
                query: {
                  document_title: response.documentProperties?.title || 'Dokument',
                  document_type: 'pdf'
                }
              })
            }
          })
          .catch((err: AxiosError) => {
            console.warn(err)
            waitingForServerResponse.value = false
          })
      })
      .catch((err) => {
        console.warn(err)
        waitingForServerResponse.value = false
      })
  }

  async function getFieldObjects(): Promise<FieldObjects> {
    if (!loadingTask.value) {
      return Promise.reject(new Error('No loadingTask'))
    }
    const pdf = await loadingTask.value.promise
    const fieldObjects = (await pdf.getFieldObjects()) as FieldObjects
    if (!fieldObjects) {
      console.warn('No fieldObjects (getFieldObjects)')
    }
    return handleNestedFields(fieldObjects)
  }

  /*
   * Handles nested fields: Since FieldObjects of nested fields have Key.Key as the
   * key which causes issues in the backend, we need to remove the last part of the
   * key and the last part of the name of the fieldObject.
   */
  function handleNestedFields(fieldObjects: FieldObjects): FieldObjects {
    const newFields: FieldObjects = {}
    for (const key in fieldObjects) {
      if (fieldObjects[key].length > 0 && key.includes('.' + key.split('.').pop())) {
        const newKey = key.replace('.' + key.split('.').pop(), '')
        fieldObjects[key].forEach((fieldObject: FieldObject) => {
          fieldObject.name = fieldObject.name.replace('.' + fieldObject.name.split('.').pop(), '')
        })
        newFields[newKey] = [...newFields[newKey], ...fieldObjects[key]]
      } else {
        newFields[key] = fieldObjects[key]
      }
    }
    return newFields
  }

  /*
   * https://github.com/mozilla/pdf.js/blob/master/src/display/annotation_storage.js
   */
  function getAnnotationStorage(): Promise<any> {
    return new Promise((resolve) => {
      if (!loadingTask.value) {
        return resolve(null)
      }
      loadingTask.value.promise.then((pdf: PDFJS.PDFDocumentProxy) => {
        resolve(pdf.annotationStorage)
      })
    })
  }

  /*
   * Find a fieldObject by its name.
   */
  function findFieldObjectByName(
    fieldObjects: FieldObjects,
    fieldName: string
  ): FieldObject | null {
    if (!fieldObjects) {
      return null
    }
    return (
      Object.values(fieldObjects)
        .flat()
        .find((field: any) => field.name === fieldName) ?? null
    )
  }

  ///////////////////////////////////////////////////////////////////////////////////
  /////  LIFECYCLE HOOKS  ///////////////////////////////////////////////////////////
  ///////////////////////////////////////////////////////////////////////////////////

  /*
   * Happens on any change to the annotationLayer.
   */
  function onAnnotation(update: AnnotationUpdate): void {
    if (!loadingTask.value) {
      throw new Error('No loadingTask')
    }

    // Handle link annotations by opening in new tab
    if (update.type === 'link') {
      if (update.data.url && typeof update.data.url === 'string') {
        window.open(update.data.url, '_blank')
      }
      return
    }
    const id: string | undefined = lt.byName.get(update.data.fieldName)

    if (!id) {
      throw new Error('Could not find annotation')
    }

    // For checkboxes, convert boolean checked value to "/Yes" or "/Off" for backend compatibility
    let value: string
    if (update.type === 'form-checkbox') {
      value = update.data.checked ? '/Yes' : '/Off'
    } else {
      const dataValue = update.data.value
      value = dataValue != null ? String(dataValue) : ''
    }

    annotationStorage.value = {
      ...annotationStorage.value,
      [id]: {
        value: value
      }
    }
  }

  /*
   * Vue lifecycle hook.
   */
  function doMount(): void {
    try {
      if (typeof window === 'undefined' || !('Worker' in window)) {
        throw new Error('Web Workers not supported in this environment.')
      }
    } catch (error) {
      console.warn(error)
    }

    switch (route.name) {
      case 'PDF Documents':
        initClientResponse()
        break
      case 'External PDF Documents':
        initClientResponse()
        break
      case 'Legacy Document Detail':
        initClientResponse()
        break
      case 'Legacy External Document Detail':
        initClientResponse()
        break
      case 'PDF Template Detail':
        initClientResponseV3()
        break
      case 'External PDF Template Detail':
        initClientResponseV3()
        break
      case 'debug-view':
        loadPDF('/probably_acroform_ok12.pdf')
        break
    }
  }

  /*
   * Called by PDFDisplay.vue when VuePDF has loaded the PDF.
   * See loadPDF() where the loading procedure starts.
   */
  async function onLoaded(_value: PDFJS.PageViewport): Promise<void> {
    try {
      signatureStore.setPdfStore('old')
      const fieldObjects = await getFieldObjects()
      await signatureStore.updateAll(fieldObjects)
      await signatureStore.findAllSignatureFields(fieldObjects)
    } catch (error) {
      console.warn(error)
    } finally {
      ready.value = true
    }
  }

  ///////////////////////////////////////////////////////////////////////////////////
  /////  PDFJS MAIN  ////////////////////////////////////////////////////////////////
  ///////////////////////////////////////////////////////////////////////////////////

  /*
   * Load a PDF from a given source.
   */
  async function loadPDF(src: Parameters<typeof PDFJS.getDocument>[0]): Promise<void | Error> {
    if (!src) {
      isLoadingPDF.value = false
      return Promise.reject<Error>(new Error('No URL provided.'))
    }

    if (!PDFJS.GlobalWorkerOptions?.workerSrc) {
      // todo: this is a hack to get the worker to load. Ideally, this should come from our own server.
      PDFJS.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${PDFJS.version}/legacy/build/pdf.worker.min.mjs`
    }

    isLoadingPDF.value = true
    loadingTask.value = PDFJS.getDocument({
      url: src,
      cMapUrl: CMAP_URL,
      cMapPacked: CMAP_PACKED
    })
    loadingTask.value.onProgress = (progressData: { loaded: number; total: number }) => {
      if (progressData.total && progressData.total > 0) {
        loadingProgress.value = (progressData.loaded / progressData.total) * 100
      } else {
        loadingProgress.value = 0
      }
    }
    const t0 = performance.now()
    return await handlePDF()
      .then(() => {})
      .catch((err) => {
        console.warn(
          `%cFailure. ${performance.now() - t0}`,
          'background: #ce6758; color: #000; padding: 4px;',
          err
        )
      })
      .finally(() => {
        isLoadingPDF.value = false
      })
  }

  /*
   * All the steps after the PDF has been loaded are orchestrated here.
   */
  async function handlePDF(): Promise<void> {
    if (!loadingTask.value) {
      console.warn('No loadingTask')
      return
    }
    const pdf: PDFJS.PDFDocumentProxy = await loadingTask.value.promise

    pages.value = pdf.numPages
    fingerprints.value = pdf.fingerprints

    const fieldObjects = (await pdf.getFieldObjects()) as FieldObjects
    if (!fieldObjects) {
      return
    }

    // The rest will only happen for PDFS with annotations.

    // Set up the lookup table
    getEntries(fieldObjects).forEach(([, value]) => {
      value.forEach((fieldObject: FieldObject) => {
        lt.byId.set(fieldObject.id, fieldObject.name)
        lt.byName.set(fieldObject.name, fieldObject.id)
      })
    })

    // Initialize and prefill the annotationStorage
    if (
      selectedForm.value &&
      selectedForm.value.documentProperties.id &&
      annotationStorages.value.get(selectedForm.value.documentProperties.id)
    ) {
      if (annotationStorages.value.has(selectedForm.value.documentProperties.id)) {
        annotationStorage.value = annotationStorages.value.get(
          selectedForm.value.documentProperties.id
        )
      }
    } else {
      annotationStorage.value = {
        ...(await makeAnnotationStorage(fieldObjects)),
        ...getPrefillData(fieldObjects)
      }
    }

    // TODO Make sure handleKids works as intended
    handleKidIds(fieldObjects, annotationStorage.value)

    // Do stuff after the PDF has been fully loaded
    const { promise: annotationLayerPromise, cancel: cancelAnnotationLayer } =
      waitForElement('.annotationLayer')
    try {
      const annotationLayer = await annotationLayerPromise
      await waitForAnnotations(fieldObjects)
      handleRequiredFields(annotationLayer)
      await signatureStore.updateAll(fieldObjects)
    } finally {
      cancelAnnotationLayer()
    }
  }

  /*
   * Sends the client token to the backend to get the document.
   * (ClientResponseView only)
   */
  function initClientResponse(): void {
    const data: RequestFormPayload = {
      token: route.params.id as string,
      access_token: route.query.access_token?.toString() || ''
    }

    access_token.value = route.query.access_token?.toString() || ''
    session_token.value = route.query.session_token?.toString() || ''
    ClientActionService.retrievePdfDocument(data)
      .then((response: AxiosResponse) => {
        selectedForm.value = response.data.payload.document
        loadPDF(response.data.payload.document.documentProperties.download.url)
      })
      .catch((err: any) => {
        console.warn(err)
      })
      .finally(() => {
        initDone.value = true
      })
  }

  /*
   * Sends the client token to the backend to get the document.
   * (ClientResponseView only)
   */
  function initClientResponseV3(): void {
    access_token.value = route.query.access_token?.toString() || ''
    session_token.value = route.query.session_token?.toString() || ''
    FormsService.formsPdfFormRetrieve({
      token: (route.params.form_token as string) || (route.params.id as string)
    })
      .then((response) => {
        selectedForm.value = response
        loadPDF(response.documentProperties.download.url)
      })
      .catch((err: any) => {
        console.warn(err)
      })
      .finally(() => {
        initDone.value = true
      })
  }

  ///////////////////////////////////////////////////////////////////////////////////
  /////  ANNOTATION STORAGE  ////////////////////////////////////////////////////////
  ///////////////////////////////////////////////////////////////////////////////////

  /*
   * Returns an annotationStorage containing the prefill data for the selected
   * mandate.
   */
  function getPrefillData(fieldObjects: FieldObjects): AnnotationStorage {
    if (!fieldObjects) {
      console.warn('No fieldObjects (getPrefillData)')
      return {}
    }

    function fieldIds(fieldName: string) {
      if (!fieldObjects[fieldName]) {
        return []
      }
      return fieldObjects[fieldName].map((fo: FieldObject) => fo.id)
    }

    function makeObj(key: string, value: string): { [x: string]: AnnotationObject } {
      return {
        [key]: {
          value: value ?? findFieldObjectByName(fieldObjects, key)?.defaultValue ?? 'NONE'
        }
      }
    }

    // If no prefill data provided, try to get it from clientActionStore
    const defaultPrefillData = prefilledData.value || {
      client_first_name: clientActionStore.clientAction?.data.client.first_name ?? '',
      client_last_name: clientActionStore.clientAction?.data.client.last_name ?? '',
      client_email: clientActionStore.clientAction?.data.client.email ?? '',
      client_phone: clientActionStore.clientAction?.data.client.phone ?? '',
      probably_client_city: clientActionStore.clientAction?.data.client.city ?? '',
      probably_client_zip: clientActionStore.clientAction?.data.client.zip ?? '',
      probably_client_street: clientActionStore.clientAction?.data.client.street ?? '',
      probably_client_street_number:
        clientActionStore.clientAction?.data.client.street_number ?? '',
      probably_client_date_of_birth: clientActionStore.clientAction?.data.client.date_of_birth ?? ''
    }

    const rawPrefillData = Object.entries(defaultPrefillData)

    return rawPrefillData
      .flatMap(([key, value]) => {
        return fieldIds(key).map((id) => {
          return [id, value]
        })
      })
      .reduce((acc, [id, value]) => {
        return {
          ...acc,
          ...makeObj(id as string, value as string)
        }
      }, {})
  }

  /**
   * Creates an annotation storage object from the given field objects.
   * @param fieldObjects - The field objects to create the annotation storage from.
   * @returns A promise that resolves with the annotation storage object.
   * @throws An error if there is no loading task or no field objects.
   */
  function makeAnnotationStorage(fieldObjects: FieldObjects): Promise<AnnotationStorage> {
    return new Promise((resolve, reject) => {
      if (!loadingTask.value) {
        reject(new Error('No loadingTask.'))
        return
      }

      if (!fieldObjects) {
        reject(new Error('No fieldObjects (makeAnnotationStorage'))
        return
      }

      // Map each fieldObject to an object with its id as the key and its
      // value as the value, then reduce the array of objects into a single
      // object.
      resolve(
        getEntries(fieldObjects)
          .flatMap(([, value]) => {
            return value.map((fieldObject: FieldObject) => {
              return {
                [fieldObject.id]: {
                  value: fieldObject.value // current value
                }
              }
            })
          })
          .reduce((acc, obj) => ({ ...acc, ...obj }), {})
      )
    })
  }

  /*
   * If a fieldObject has kidIds, the value of the parent fieldObject is
   * propagated to the kidIds.
   *
   * Modifies annotationStorage
   */
  function handleKidIds(fieldObjects: FieldObjects, prefillData: any): void {
    if (!fieldObjects) {
      console.warn('No fieldObjects (handleKids)')
      return
    }
    Object.entries(prefillData).forEach(([key, value]) => {
      if (Object.hasOwnProperty.call(fieldObjects, key)) {
        const fo: FieldObject | undefined = fieldObjects[key].find(
          (f: FieldObject) => f.name === key
        )
        if (fo) {
          if (fo.kidIds) {
            fo.kidIds.forEach((kidId: string) => {
              const kid: FieldObject | undefined = fieldObjects[key].find(
                (f: any) => f.id === kidId
              )
              if (kid && annotationStorage.value) {
                annotationStorage.value[kid.id] = {
                  value: value as string
                }
              }
            })
          } else {
            if (annotationStorage.value) {
              annotationStorage.value[fo.id] = {
                value: value as string
              }
            }
          }
        }
      }
    })
  }

  ///////////////////////////////////////////////////////////////////////////////////
  /////  REQUIRED FIELDS/////////////////////////////////////////////////////////////
  ///////////////////////////////////////////////////////////////////////////////////

  /*
   * Store all required fields in the requiredFields ref.
   */
  function handleRequiredFields(annotationLayer: Element): void {
    requiredFields.value = Array.from(annotationLayer.querySelectorAll('input[required]')).map(
      (x) => {
        return x.getAttribute('data-element-id')
      }
    )
  }

  ///////////////////////////////////////////////////////////////////////////////////
  /////  LOADING/UNLOADING  /////////////////////////////////////////////////////////
  ///////////////////////////////////////////////////////////////////////////////////

  // TODO Might have unintended side effects... Should change selectedForm instead
  async function loadQuickView(url: string): Promise<void> {
    unloadPdf()
    loadPDF(url)
  }

  function unloadPdf(): void {
    loadingTask.value?.destroy()
    loadingTask.value = undefined
    selectedForm.value = undefined
    pages.value = 1
    page.value = 1
  }

  function reload(): void {
    fingerprints.value = []
    unloadPdf()
    requiredFields.value = []
    ready.value = false
    hasChanges.value = false
    hasSubmitted.value = false
    isLoadingPDF.value = false
    showConfirmModal.value = false
    waitingForServerResponse.value = false
    annotationStorage.value = undefined
    annotationStorages.value = new Map()
    doMount()
  }

  return {
    // load and init stuff
    onLoaded,
    unloadPdf,
    resetForm,

    // select form
    loadPDF,
    loadQuickView,
    // selectAnyForm,
    selectedForm,
    selectForm,
    // selectRequestableFormById,

    // for VuePDF
    onAnnotation,

    // page stuff
    nextPage,
    page,
    gotoPage,
    pages,

    // data state
    annotationStorage,
    annotationStorages,

    // have state
    loadingTask,
    requiredFields,
    prevPage,

    // ready state
    ready,
    waitingForServerResponse,
    hasChanges,
    hasAllRequired,
    editable,
    loadingProgress,
    initDone,
    isLoadingPDF,

    submitForm,
    submitFormV3,

    // show stuff
    showConfirmModal,

    // debug
    getFieldObjects,
    getAnnotationStorage,

    //reload after email sending
    reload,

    doMount
  }
})
